import api from '../../utils/api';
import { overrideTimezone } from '../../utils/dateHelper';

import {
  closedStatuses,
  inProgressStatuses,
  upcomingStatuses
} from './../../utils/rentalStatuses';
import { fetchVehicleById } from './../../store/actions/vehicles';

export const INPROGRESS_RENTALS_FETCHED = 'INPROGRESS_RENTALS_FETCHED';
export const UPCOMING_RENTALS_FETCHED = 'UPCOMING_RENTALS_FETCHED';
export const CLOSED_RENTALS_FETCHED = 'CLOSED_RENTALS_FETCHED';
export const UPCOMING_CARPOOLINGS_FETCHED = 'UPCOMING_CARPOOLINGS_FETCHED';
export const INPROGRESS_CARPOOLINGS_FETCHED = 'INPROGRESS_CARPOOLINGS_FETCHED';
export const RENTAL_CREATED = 'RENTAL_CREATED';
export const RENTAL_UPDATED = 'RENTAL_UPDATED';
export const PASSENGER_CREATED = 'PASSENGER_CREATED';
export const PASSENGER_DELETED = 'PASSENGER_DELETED';

export const fetchAllClosedRentals = (page, driverId) => async dispatch => {
  const batches = [
    {
      statuses: closedStatuses,
      type: CLOSED_RENTALS_FETCHED,
      page: page,
      limit: 15,
      driverId: driverId,
      order: 'DESC'
    }
  ];
  try {
    const vehicleIds = new Set();

    await Promise.all(
      batches.map(async ({ statuses, type, page, limit, driverId, order }) => {
        const params = {
          page,
          limit,
          statuses: statuses,
          driver_ids: [driverId],
          order: order
        };

        const { rentals, meta } = await api.rentals.getAll(params);

        dispatch({ type, rentals, meta });

        rentals.forEach(rental => {
          if (Number.isFinite(rental.vehicle_id)) {
            vehicleIds.add(rental.vehicle_id);
          }
        });
      })
    );

    await Promise.all(
      Array.from(vehicleIds).map(id => dispatch(fetchVehicleById(id)))
    );
  } catch (error) {
    console.error('Erreur fetchAllClosedRentals:', error);
  }
};

export const fetchAllRentals = page => async dispatch => {
  const batches = [
    {
      statuses: inProgressStatuses,
      type: INPROGRESS_RENTALS_FETCHED,
      page: 1,
      limit: 3,
      order: 'ASC'
    },
    {
      statuses: upcomingStatuses,
      type: UPCOMING_RENTALS_FETCHED,
      page: page,
      limit: 20,
      order: 'ASC'
    }
  ];
  try {
    const vehicleIds = new Set();

    await Promise.all(
      batches.map(async ({ statuses, type, page, limit, order }) => {
        const params = {
          page,
          limit,
          statuses: statuses,
          order: order
        };
        const { rentals, meta } = await api.rentals.getAll(params);

        dispatch({ type, rentals, meta });

        rentals.forEach(rental => {
          if (Number.isFinite(rental.vehicle_id)) {
            vehicleIds.add(rental.vehicle_id);
          }
        });
      })
    );

    await Promise.all(
      Array.from(vehicleIds).map(id => dispatch(fetchVehicleById(id)))
    );
  } catch (error) {
    console.error('Erreur fetchAllRentals:', error);
  }
};

export const fetchAllCarpoolings = page => async dispatch => {
  const batches = [
    {
      statuses: inProgressStatuses,
      type: INPROGRESS_CARPOOLINGS_FETCHED,
      page: 1,
      limit: 3
    },
    {
      statuses: upcomingStatuses,
      type: UPCOMING_CARPOOLINGS_FETCHED,
      page: page,
      limit: 20
    }
  ];
  try {
    const vehicleIds = new Set();

    await Promise.all(
      batches.map(async ({ statuses, type, page, limit, driverId }) => {
        const params = { page, limit, statuses: statuses };
        const { rentals, meta } = await api.rentals.getAllCarpoolings(params);

        dispatch({ type, rentals, meta });

        rentals.forEach(rental => {
          if (Number.isFinite(rental.vehicle_id)) {
            vehicleIds.add(rental.vehicle_id);
          }
        });
      })
    );

    await Promise.all(
      Array.from(vehicleIds).map(id => dispatch(fetchVehicleById(id)))
    );
  } catch (error) {
    console.error('Erreur fetchAllRentals:', error);
  }
};

export function createRental({
  driverId,
  vehicleId,
  startStationId,
  endStationId,
  destinationId,
  startTime,
  endTime,
  estimatedDistance,
  rentalLabelId,
  discountCodes,
  geotracking,
  carpooling,
  carpoolingDestination
}) {
  return async function(dispatch) {
    try {
      const response = await api.rentals.createRental({
        driverId,
        vehicleId,
        startStationId,
        endStationId,
        destinationId,
        startTime: startTime.format(),
        endTime: endTime.format(),
        estimatedDistance,
        rentalLabelId,
        discountCodes,
        geotracking,
        carpooling,
        carpoolingDestination
      });
      const rental = response.rental;

      dispatch({
        type: RENTAL_CREATED,
        rental
      });
    } catch (error) {
      throw error;
    }
  };
}

export function createNewPassenger(rental, email) {
  return async function(dispatch) {
    try {
      await api.rentals.createNewPassenger(rental.id, email);

      dispatch({
        type: PASSENGER_CREATED,
        rental
      });
    } catch (error) {
      throw error;
    }
  };
}

export function createPassenger({ rental }) {
  return async function(dispatch) {
    try {
      await api.rentals.createPassenger(rental.id);

      dispatch({
        type: PASSENGER_CREATED,
        rental
      });
    } catch (error) {
      throw error;
    }
  };
}

export function deletePassenger({ rentalId, passengerId }) {
  return async function(dispatch) {
    try {
      await api.rentals.deletePassenger(rentalId, passengerId);

      dispatch({
        type: PASSENGER_DELETED
      });
    } catch (error) {
      throw error;
    }
  };
}

export function cancelRental(id) {
  return async function(dispatch) {
    try {
      await api.rentals.cancelRental(id);

      const response = await api.rentals.getOne(id);
      const rental = response.rental;

      dispatch({
        type: RENTAL_UPDATED,
        rental
      });
    } catch (error) {
      throw error;
    }
  };
}

export function updateRental({ id, startDate, endDate }) {
  return async function(dispatch) {
    try {
      const response = await api.rentals.updateRental({
        id,
        startTime: overrideTimezone(startDate).format(),
        endTime: overrideTimezone(endDate).format()
      });

      const rental = response.rental;

      dispatch({
        type: RENTAL_UPDATED,
        rental
      });
    } catch (error) {
      throw error;
    }
  };
}

export function updatePrivacyUsage({ id, geotracking }) {
  return async function(dispatch) {
    try {
      const response = await api.rentals.updatePrivacyUsage({
        id,
        geotracking: geotracking
      });

      const rental = response.rental;

      dispatch({
        type: RENTAL_UPDATED,
        rental
      });
      return response.rental;
    } catch (error) {
      throw error;
    }
  };
}
